/**
 * Poll contracts — card #77.
 *
 * A poll is a card with a poll on it, so these shapes ride on `TaskDetail`
 * rather than standing alone. Everything §6 gives a card — a due date, the
 * Inbox, My Tasks, completion, references, comments, search — a poll has for
 * free, because it *is* a card.
 *
 * **The visibility rule lives on the server, and this file is the reason it
 * has to.** #77: "Poll RESULTS SHOULD NOT be visible until AFTER the user has
 * cast their vote." A rule enforced by hiding a number in the UI is not
 * enforced at all — the number is still in the response, and the response is
 * one devtools tab away. So `tally` is **absent** from the payload until the
 * reader has earned it, and `canSeeResults` says whether it should be there.
 * Nothing downstream has to be trusted to keep a secret it was never given.
 */
import { z } from 'zod';
import { uuidSchema } from './common.js';
import { userRefSchema } from './users.js';
/** How long a poll option's label may be. One line on a button. */
export const POLL_OPTION_MAX_LENGTH = 120;
/** Why someone voted the way they did — one line, not a comment. */
export const POLL_REASON_MAX_LENGTH = 500;
/**
 * "An unlimited number of polling options" is the ask, and this is not a
 * disagreement with it — it is the bound that keeps one poll from being a
 * denial of service against every reader of it. Nothing anyone means by
 * "unlimited" is above fifty.
 */
export const POLL_OPTIONS_MAX = 50;
export const pollOptionSchema = z.object({
    id: uuidSchema,
    label: z.string(),
    /**
     * How many chose this — **absent** until the reader may see results, rather
     * than zero. Zero is a fact about the poll; absent is a fact about the
     * reader, and conflating them would leak the first while pretending not to.
     */
    votes: z.number().int().nonnegative().optional(),
    /**
     * Who chose it. Present only when the poll shows voters *and* the reader may
     * see results (#77: "allow polsters to see what other polsters have voted or
     * to hide this information and just see the results per poll option").
     */
    voters: z.array(z.object({ user: userRefSchema, reason: z.string().nullable() })).optional(),
});
export const pollSchema = z.object({
    id: uuidSchema,
    taskId: uuidSchema,
    /** The question, when the card's title is not it. */
    question: z.string().nullable(),
    options: z.array(pollOptionSchema),
    allowChange: z.boolean(),
    showVoters: z.boolean(),
    allowMultiple: z.boolean(),
    allowAbstain: z.boolean(),
    /** Null while open. Set when the card is completed. */
    closedAt: z.string().nullable(),
    createdBy: userRefSchema,
    /** The electorate, which is public: knowing who was asked is not the result. */
    voters: z.array(userRefSchema),
    /* --- The reader's own position, which is why this shape is per-request --- */
    /** Is the reader entitled to vote at all? */
    canVote: z.boolean(),
    /**
     * The option ids the reader chose. Empty and `hasVoted` true is an
     * abstention — recorded, not inferred from silence.
     */
    myVote: z.array(uuidSchema),
    myReason: z.string().nullable(),
    hasVoted: z.boolean(),
    /**
     * May the reader still change their mind? False once a poll that locks votes
     * has one from them, and false once it is closed. The UI says so *before*
     * the vote is cast, which is #77's "the polster should be told ahead of
     * time".
     */
    canChangeVote: z.boolean(),
    /** Whether `votes` and `voters` are populated above. */
    canSeeResults: z.boolean(),
    /* --- Turnout, which everyone sees, because it is not the result --- */
    /**
     * How many of the electorate have voted, and how many there are. Visible
     * even before you have voted: it is what tells the creator when to close the
     * poll, and it gives away nothing about which way it is going.
     */
    votedCount: z.number().int().nonnegative(),
    voterCount: z.number().int().nonnegative(),
});
const optionLabelSchema = z.string().trim().min(1).max(POLL_OPTION_MAX_LENGTH);
/** Turn a card into a poll — #77's "create a card, turn that card into a poll". */
export const createPollRequestSchema = z
    .object({
    question: z.string().trim().max(500).nullish(),
    /** At least two: one option is not a question, it is an announcement. */
    options: z.array(optionLabelSchema).min(2).max(POLL_OPTIONS_MAX),
    voterIds: z.array(uuidSchema).default([]),
    /**
     * Poll everybody who can see the card, rather than naming them — #93.
     *
     * A flag rather than a magic id in `voterIds`, and resolved on the server:
     * only the server knows who can see a card in a private project, and the
     * answer must not be a client's guess. It is a *snapshot* taken when the
     * poll opens, for the same reason recurrence spawns one occurrence at a
     * time (D15): "6 of 9 voted" has to keep meaning what it said, and an
     * electorate that grows with the staff list would silently change the
     * question after people had answered it.
     */
    everyone: z.boolean().default(false),
    allowChange: z.boolean().default(true),
    showVoters: z.boolean().default(false),
    allowMultiple: z.boolean().default(false),
    allowAbstain: z.boolean().default(true),
})
    .strict();
/**
 * Change a poll's settings, its options or its electorate.
 *
 * Options are replaced wholesale by label, because that is the only edit that
 * can be expressed honestly: an option someone has already voted for cannot be
 * renamed into a different question without silently moving their vote. The
 * service refuses to remove one that has votes on it and says which.
 */
export const updatePollRequestSchema = z
    .object({
    question: z.string().trim().max(500).nullish(),
    options: z.array(optionLabelSchema).min(2).max(POLL_OPTIONS_MAX).optional(),
    voterIds: z.array(uuidSchema).optional(),
    /**
     * Extend the electorate to everyone who can see the card — #93.
     *
     * On an open poll this only ever *adds*. `setVoters` deletes a removed
     * voter's vote along with their row (#77's rule, and the right one for a
     * deliberate removal), but "ask everyone" is not a removal: a poll that
     * lost cast votes because more people were invited would punish the
     * creator for widening it. So the electorate becomes the union of who is
     * already in it and who can see the card, and nobody's answer is lost.
     */
    everyone: z.boolean().optional(),
    allowChange: z.boolean().optional(),
    showVoters: z.boolean().optional(),
    allowMultiple: z.boolean().optional(),
    allowAbstain: z.boolean().optional(),
})
    .strict();
/**
 * Cast, change or withdraw a vote.
 *
 * An empty `optionIds` with `abstain: true` is the recorded abstention. An
 * empty one without it is a withdrawal, which is only allowed where changing a
 * vote is.
 */
export const castVoteRequestSchema = z
    .object({
    optionIds: z.array(uuidSchema).max(POLL_OPTIONS_MAX),
    abstain: z.boolean().default(false),
    reason: z.string().trim().max(POLL_REASON_MAX_LENGTH).nullish(),
})
    .strict();
//# sourceMappingURL=polls.js.map